import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, MapPin, BedDouble, Bath, Ruler, School, Calendar, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { fetchProperty, createEnquiry, formatZAR } from "@/lib/api";
import { PROPERTY } from "@/lib/testIds";

export default function PropertyDetail() {
  const { id } = useParams();
  const { data: property, isLoading } = useQuery({
    queryKey: ["property", id],
    queryFn: () => fetchProperty(id),
  });

  const [selectedImage, setSelectedImage] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  const mutation = useMutation({
    mutationFn: createEnquiry,
    onSuccess: () => {
      toast.success("Enquiry sent!", { description: "Our team will be in touch shortly." });
      setForm({ name: "", email: "", phone: "", message: "" });
    },
    onError: () => toast.error("Something went wrong — please try again."),
  });

  if (isLoading) {
    return <div className="max-w-7xl mx-auto px-4 py-24 text-center text-muted-foreground">Loading property…</div>;
  }
  if (!property) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-24 text-center">
        <h2 className="font-serif text-3xl">Property not found</h2>
        <Button asChild className="mt-6 rounded-sm"><Link to="/leasing">Back to listings</Link></Button>
      </div>
    );
  }

  const isLeasing = property.listing_type === "leasing";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
      <Button asChild variant="ghost" className="mb-6 px-0 hover:bg-transparent">
        <Link to={isLeasing ? "/leasing" : "/sales"} data-testid="property-back-link">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to {isLeasing ? "leasing" : "sales"}
        </Link>
      </Button>

      <div className="grid lg:grid-cols-12 gap-10">
        {/* Gallery */}
        <div className="lg:col-span-8">
          <div className="aspect-[16/10] overflow-hidden bg-muted">
            <img src={property.images?.[selectedImage]} alt={property.title} className="w-full h-full object-cover" />
          </div>
          {property.images?.length > 1 && (
            <div className="mt-3 grid grid-cols-4 gap-3">
              {property.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedImage(i)}
                  className={`aspect-[4/3] overflow-hidden ${i === selectedImage ? "ring-2 ring-primary" : "opacity-70 hover:opacity-100"}`}
                  data-testid={`property-thumb-${i}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <div className="mt-10">
            <span className="overline text-secondary">{isLeasing ? "For Lease" : "For Sale"}</span>
            <h1 className="mt-3 font-serif text-3xl md:text-4xl lg:text-5xl leading-tight">{property.title}</h1>
            <p className="mt-3 text-muted-foreground flex items-center gap-2">
              <MapPin className="w-4 h-4" /> {property.address}, {property.city}
            </p>

            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat icon={BedDouble} label="Bedrooms" value={property.bedrooms} />
              <Stat icon={Bath} label="Bathrooms" value={property.bathrooms} />
              {property.size_sqm && <Stat icon={Ruler} label="Size" value={`${property.size_sqm} m²`} />}
              {property.nearby_institution && <Stat icon={School} label="Institution" value={property.nearby_institution} />}
            </div>

            <div className="mt-10">
              <h2 className="font-serif text-2xl">About this property</h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">{property.description}</p>
            </div>

            {property.features?.length > 0 && (
              <div className="mt-10">
                <h2 className="font-serif text-2xl">Features & Amenities</h2>
                <ul className="mt-5 grid sm:grid-cols-2 gap-3">
                  {property.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-secondary" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {property.available_from && (
              <div className="mt-10 border-l-2 border-secondary pl-4">
                <p className="overline text-secondary">Availability</p>
                <p className="mt-1 font-serif text-lg flex items-center gap-2"><Calendar className="w-4 h-4" /> {property.available_from}</p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar / enquire form */}
        <aside className="lg:col-span-4">
          <div className="sticky top-28 bg-background border border-border p-8">
            <p className="overline text-secondary">{isLeasing ? "Rent from" : "Asking Price"}</p>
            <p className="mt-2 font-serif text-3xl">
              {formatZAR(property.price)}
              {isLeasing && <span className="font-sans text-base text-muted-foreground"> /month</span>}
            </p>

            <div className="my-6 h-px bg-border" />

            <h3 className="font-serif text-xl">Enquire about this property</h3>
            <form
              className="mt-5 space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                mutation.mutate({
                  ...form,
                  property_id: property.id,
                  enquiry_type: isLeasing ? "leasing" : "sales",
                });
              }}
            >
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-sm" data-testid={PROPERTY.enquireName} />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 rounded-sm" data-testid={PROPERTY.enquireEmail} />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 rounded-sm" data-testid={PROPERTY.enquirePhone} />
              </div>
              <div>
                <Label htmlFor="message">Message</Label>
                <Textarea id="message" required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 rounded-sm" data-testid={PROPERTY.enquireMessage} />
              </div>
              <Button type="submit" disabled={mutation.isPending} className="w-full rounded-sm h-11 bg-primary text-primary-foreground" data-testid={PROPERTY.enquireSubmit}>
                {mutation.isPending ? "Sending…" : "Send Enquiry"}
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                Or email us at{" "}
                <a href="mailto:info@thecampusco.co.za" className="underline">info@thecampusco.co.za</a>
              </p>
            </form>
          </div>
        </aside>
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="border border-border p-4">
    <Icon className="w-4 h-4 text-secondary" />
    <p className="mt-3 overline text-[10px] text-muted-foreground">{label}</p>
    <p className="mt-1 font-serif text-lg truncate">{value}</p>
  </div>
);
